/**
 * ChatGPT Apps SDK Integration
 *
 * Hooks and utilities for integrating Zendir UI components with ChatGPT's
 * window.openai API. Based on: https://developers.openai.com/apps-sdk/build/chatgpt-ui/
 *
 * Key capabilities:
 * - window.openai.toolInput / toolOutput - Tool data from MCP server
 * - window.openai.widgetState / setWidgetState - Persistent UI state
 * - window.openai.callTool - Invoke other MCP tools from widget
 * - window.openai.sendFollowUpMessage - Insert user messages
 * - window.openai.theme - Light/dark theme detection
 * - window.openai.locale - Localization
 * - window.openai.requestDisplayMode - Fullscreen/PiP modes
 * - window.openai.notifyIntrinsicHeight - Dynamic height reporting
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from "react";

// ============================================================================
// Type Definitions
// ============================================================================

export interface OpenAiGlobals {
  /** Arguments supplied when the tool was invoked */
  toolInput?: Record<string, unknown>;
  /** Your structuredContent - the model reads this */
  toolOutput?: Record<string, unknown>;
  /** Metadata payload - only widget sees it */
  toolResponseMetadata?: Record<string, unknown>;
  /** Snapshot of UI state persisted between renders */
  widgetState?: Record<string, unknown>;
  /** Current theme */
  theme?: "light" | "dark";
  /** Current locale (e.g., 'en-US') */
  locale?: string;
  /** Display mode */
  displayMode?: "inline" | "pip" | "fullscreen";
  /** Maximum allowed height */
  maxHeight?: number;
  /** View type */
  view?: "conversation" | "modal";
  /** User agent info */
  userAgent?: string;
  /** Safe area insets */
  safeArea?: { top: number; bottom: number; left: number; right: number };

  // API Methods
  setWidgetState?: (state: Record<string, unknown>) => void;
  callTool?: (name: string, args: Record<string, unknown>) => Promise<unknown>;
  sendFollowUpMessage?: (options: { prompt: string }) => Promise<void>;
  uploadFile?: (file: File) => Promise<{ fileId: string }>;
  getFileDownloadUrl?: (options: {
    fileId: string;
  }) => Promise<{ downloadUrl: string }>;
  requestDisplayMode?: (options: {
    mode: "inline" | "pip" | "fullscreen";
  }) => Promise<void>;
  requestModal?: (options: unknown) => Promise<void>;
  notifyIntrinsicHeight?: (height: number) => void;
  openExternal?: (options: { href: string }) => void;
  requestClose?: () => void;
}

declare global {
  interface Window {
    openai?: OpenAiGlobals;
  }
}

// ============================================================================
// Core Hook: useOpenAiGlobal
// ============================================================================

const SET_GLOBALS_EVENT_TYPE = "openai:set_globals";

interface SetGlobalsEvent extends CustomEvent {
  detail: { globals: Partial<OpenAiGlobals> };
}

/**
 * Subscribe to a specific window.openai global value
 */
export function useOpenAiGlobal<K extends keyof OpenAiGlobals>(
  key: K,
): OpenAiGlobals[K] {
  const getSnapshot = useCallback(() => {
    return window.openai?.[key];
  }, [key]);

  const subscribe = useCallback(
    (onChange: () => void) => {
      const handleSetGlobal = (event: Event) => {
        const customEvent = event as SetGlobalsEvent;
        if (customEvent.detail?.globals?.[key] !== undefined) {
          onChange();
        }
      };

      window.addEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal, {
        passive: true,
      });
      return () =>
        window.removeEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal);
    },
    [key],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ============================================================================
// Widget State Hook
// ============================================================================

type SetStateAction<T> = T | ((prevState: T) => T);

/**
 * Manage widget state that persists across renders via window.openai.setWidgetState
 */
export function useWidgetState<T extends Record<string, unknown>>(
  defaultState: T | (() => T),
): readonly [T, (state: SetStateAction<T>) => void] {
  const widgetStateFromWindow = useOpenAiGlobal("widgetState") as T | undefined;

  const [widgetState, _setWidgetState] = useState<T>(() => {
    if (widgetStateFromWindow != null) {
      return widgetStateFromWindow;
    }
    return typeof defaultState === "function"
      ? (defaultState as () => T)()
      : defaultState;
  });

  useEffect(() => {
    if (widgetStateFromWindow != null) {
      _setWidgetState(widgetStateFromWindow);
    }
  }, [widgetStateFromWindow]);

  const setWidgetState = useCallback((state: SetStateAction<T>) => {
    _setWidgetState((prevState: T) => {
      const newState =
        typeof state === "function" ? (state as (p: T) => T)(prevState) : state;
      window.openai?.setWidgetState?.(newState);
      return newState;
    });
  }, []);

  return [widgetState, setWidgetState] as const;
}

// ============================================================================
// Tool Output Hook
// ============================================================================

/**
 * Read the tool output (structuredContent) from the MCP server response
 */
export function useToolOutput<T = Record<string, unknown>>(): T | undefined {
  return useOpenAiGlobal("toolOutput") as T | undefined;
}

/**
 * Read the tool input (arguments) from the tool invocation
 */
export function useToolInput<T = Record<string, unknown>>(): T | undefined {
  return useOpenAiGlobal("toolInput") as T | undefined;
}

// ============================================================================
// Theme Hook
// ============================================================================

export type ThemeMode = "light" | "dark";

/**
 * Get current ChatGPT theme and provide CSS-friendly values
 * Named useChatGPTTheme to avoid conflict with the core useTheme from ThemeProvider
 */
export function useChatGPTTheme(): {
  theme: ThemeMode;
  isDark: boolean;
  colors: {
    background: string;
    surface: string;
    surfaceHover: string;
    border: string;
    text: string;
    textMuted: string;
    accent: string;
  };
} {
  const theme = useOpenAiGlobal("theme") ?? "dark";
  const isDark = theme === "dark";

  return {
    theme,
    isDark,
    colors: isDark
      ? {
          background: "#09090b",
          surface: "#18181b",
          surfaceHover: "#27272a",
          border: "#3f3f46",
          text: "#fafafa",
          textMuted: "#a1a1aa",
          accent: "#22d3ee",
        }
      : {
          background: "#ffffff",
          surface: "#f4f4f5",
          surfaceHover: "#e4e4e7",
          border: "#d4d4d8",
          text: "#18181b",
          textMuted: "#636370", // WCAG AA: 5.90:1 on #fff, 5.39:1 on #f4f4f5, 4.66:1 on #e4e4e7 (was #71717a → 4.49:1 FAIL on surface)
          accent: "#0ea5e9",
        },
  };
}

// ============================================================================
// Locale Hook
// ============================================================================

/**
 * Get current locale for internationalization
 */
export function useLocale(): string {
  return useOpenAiGlobal("locale") ?? "en-US";
}

// ============================================================================
// Display Mode Hook
// ============================================================================

/**
 * Manage widget display mode (inline, PiP, fullscreen)
 */
export function useDisplayMode(): {
  mode: "inline" | "pip" | "fullscreen";
  maxHeight?: number;
  requestMode: (mode: "inline" | "pip" | "fullscreen") => Promise<void>;
  close: () => void;
} {
  const mode = useOpenAiGlobal("displayMode") ?? "inline";
  const maxHeight = useOpenAiGlobal("maxHeight");

  const requestMode = useCallback(
    async (newMode: "inline" | "pip" | "fullscreen") => {
      await window.openai?.requestDisplayMode?.({ mode: newMode });
    },
    [],
  );

  const close = useCallback(() => {
    window.openai?.requestClose?.();
  }, []);

  return { mode, maxHeight, requestMode, close };
}

// ============================================================================
// Tool Calling Hook
// ============================================================================

/**
 * Call another MCP tool from within a widget
 */
export function useCallTool(): {
  callTool: <T = unknown>(
    name: string,
    args: Record<string, unknown>,
  ) => Promise<T>;
  isLoading: boolean;
  error: Error | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const callTool = useCallback(
    async <T = unknown>(
      name: string,
      args: Record<string, unknown>,
    ): Promise<T> => {
      if (!window.openai?.callTool) {
        throw new Error("window.openai.callTool not available");
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await window.openai.callTool(name, args);
        return result as T;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { callTool, isLoading, error };
}

// ============================================================================
// Follow-up Message Hook
// ============================================================================

/**
 * Send a follow-up message as if the user asked it
 */
export function useSendMessage(): (prompt: string) => Promise<void> {
  return useCallback(async (prompt: string) => {
    await window.openai?.sendFollowUpMessage?.({ prompt });
  }, []);
}

// ============================================================================
// Dynamic Height Hook
// ============================================================================

/**
 * Get the maximum height available for the widget from the host environment.
 * This is the CORRECT pattern per OpenAI Apps SDK examples.
 *
 * The host (ChatGPT/MCPJam) provides maxHeight, and widgets should constrain
 * themselves to this height. Widgets should NOT report their height back.
 *
 * @returns The maximum height in pixels, or undefined if not available
 */
export function useMaxHeight(): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(
    () => window.openai?.maxHeight,
  );

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.detail?.globals && "maxHeight" in event.detail.globals) {
        setMaxHeight(event.detail.globals.maxHeight);
      }
    };

    window.addEventListener("openai:set_globals", handler as EventListener);
    return () =>
      window.removeEventListener(
        "openai:set_globals",
        handler as EventListener,
      );
  }, []);

  return maxHeight;
}

// ============================================================================
// External Link Hook
// ============================================================================

/**
 * Open vetted external links in the user's browser
 */
export function useOpenExternal(): (href: string) => void {
  return useCallback((href: string) => {
    window.openai?.openExternal?.({ href });
  }, []);
}

// ============================================================================
// Utility: Check if running in ChatGPT
// ============================================================================

export function isInChatGPT(): boolean {
  return typeof window !== "undefined" && window.openai !== undefined;
}

// ============================================================================
// Re-exports (types are already exported above)
// ============================================================================
